import type { Expense, ExpenseRecurrence } from "../types";

export interface ParsedVoiceExpense {
  amount: number;
  category: string;
  vendor: string;
  description: string;
  date: string;
  recurrence: ExpenseRecurrence;
  nextBillingDate?: string;
  paymentMethod: string;
  notes: string;
  rawTranscript: string;
  confidence: number;
}

const CATEGORY_KEYWORDS: Record<string, string[]> = {
  "Software": [
    "software", "saas", "subscription", "tool", "app", "figma", "github",
    "adobe", "chatgpt", "openai", "cursor", "copilot", "slack", "notion",
    "jira", "zoom", "canva", "jetbrains", "licence", "license", "claude",
    "claude code", "anthropic"
  ],
  "Infrastructure": [
    "infrastructure", "cloud", "server", "hosting", "aws", "amazon", "gcp",
    "google cloud", "azure", "digitalocean", "vercel", "database", "render",
    "supabase", "s3", "ec2", "vps", "server fee"
  ],
  "Domain & DNS": [
    "domain", "dns", "cloudflare", "godaddy", "namecheap", "domains", "ssl"
  ],
  "Marketing & Ads": [
    "marketing", "ad", "ads", "advertising", "meta", "facebook", "instagram",
    "google ads", "campaign", "promotion", "seo", "influencer", "ad spend"
  ],
  "Office & Operations": [
    "office", "rent", "desk", "coworking", "electricity", "power", "internet",
    "wifi", "chai", "tea", "coffee", "snack", "snacks", "food", "lunch", "dinner",
    "supplies", "stationery", "water", "cleaning", "maintenance"
  ],
  "Salaries & Contractors": [
    "salary", "salaries", "stipend", "contractor", "freelancer", "intern",
    "payout", "developer", "designer", "writer", "consultant fee", "payroll"
  ],
  "Professional Services": [
    "legal", "ca", "chartered accountant", "audit", "lawyer", "filing",
    "compliance", "tax advisory", "accounting fee"
  ],
  "Travel & Transport": [
    "travel", "uber", "ola", "cab", "taxi", "flight", "hotel", "auto",
    "petrol", "fuel", "train", "metro", "bus"
  ],
};

const KNOWN_VENDORS = [
  "Claude Code", "Anthropic", "Claude Pro", "AWS", "Amazon Web Services",
  "Google Cloud", "Google Workspace", "Vercel", "GitHub", "Cloudflare",
  "Figma", "OpenAI", "ChatGPT", "Swiggy", "Zomato", "Uber", "Ola",
  "WeWork", "Starbucks", "Airtel", "Jio", "GoDaddy", "Namecheap",
  "Canva", "Adobe", "Notion", "Slack", "DigitalOcean", "Razorpay",
  "Stripe", "Apple", "Cursor"
];

function extractAmount(text: string): number {
  // 1. Direct digits with rupee / rs / inr / amount or standalone
  // e.g. "1500 rupees", "rs 450", "inr 2400", "₹5000", "paid 3500", "$20"
  const patterns = [
    /(?:rs\.?|inr|₹|rupees?|\$)\s*(\d+(?:[,\.]\d+)?)/i,
    /(\d+(?:[,\.]\d+)?)\s*(?:rs\.?|inr|₹|rupees?|bucks|dollars?)/i,
    /(?:paid|spent|cost|amount|bill|fee|subscription)\s*(?:of)?\s*(\d+(?:[,\.]\d+)?)/i,
    /\b(\d{2,6})\b/
  ];

  for (const pat of patterns) {
    const match = text.match(pat);
    if (match && match[1]) {
      const cleaned = match[1].replace(/,/g, "");
      const val = parseFloat(cleaned);
      if (!isNaN(val) && val > 0) {
        // If mentioned in dollars like $20, approximate to ~1750 INR
        if (text.includes("$") || text.toLowerCase().includes("dollar")) {
          return val <= 100 ? Math.round(val * 87) : val;
        }
        return val;
      }
    }
  }

  // 2. Word-based number conversions: "fifteen hundred", "two thousand", "four hundred fifty"
  const lower = text.toLowerCase();
  const wordMap: Record<string, number> = {
    "hundred": 100,
    "thousand": 1000,
    "lakh": 100000,
    "one": 1, "two": 2, "three": 3, "four": 4, "five": 5,
    "six": 6, "seven": 7, "eight": 8, "nine": 9, "ten": 10,
    "fifteen": 15, "twenty": 20, "thirty": 30, "forty": 40, "fifty": 50
  };

  if (lower.includes("thousand")) {
    const match = lower.match(/(one|two|three|four|five|six|seven|eight|nine|ten|twenty|thirty|forty|fifty|\d+)\s*thousand/);
    if (match) {
      const mult = wordMap[match[1]] || parseInt(match[1], 10) || 1;
      return mult * 1000;
    }
  }

  if (lower.includes("hundred")) {
    const match = lower.match(/(one|two|three|four|five|six|seven|eight|nine|ten|fifteen|twenty|\d+)\s*hundred/);
    if (match) {
      const mult = wordMap[match[1]] || parseInt(match[1], 10) || 1;
      return mult * 100;
    }
  }

  return 0;
}

function extractCategory(text: string): string {
  const lower = text.toLowerCase();
  let bestCategory = "Software";
  let maxMatches = 0;

  for (const [cat, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    let count = 0;
    for (const kw of keywords) {
      if (lower.includes(kw)) count++;
    }
    if (count > maxMatches) {
      maxMatches = count;
      bestCategory = cat;
    }
  }

  return bestCategory;
}

function extractVendor(text: string, category: string): string {
  const lower = text.toLowerCase();

  // Match known vendors directly
  for (const v of KNOWN_VENDORS) {
    if (lower.includes(v.toLowerCase())) {
      return v;
    }
  }

  // Preposition patterns: "to <Vendor>", "for <Vendor>", "from <Vendor>", "at <Vendor>"
  const prepMatch = text.match(/(?:to|from|for|at|paid to)\s+([A-Za-z0-9\s&]+?)(?:\s+(?:for|via|by|through|today|yesterday|rs|rupees|\d+)|$)/i);
  if (prepMatch && prepMatch[1]) {
    const vendorCandidate = prepMatch[1].trim();
    if (vendorCandidate.length > 2 && vendorCandidate.length < 35) {
      return vendorCandidate.charAt(0).toUpperCase() + vendorCandidate.slice(1);
    }
  }

  // Fallbacks by category
  if (category === "Infrastructure") return "Cloud Provider";
  if (category === "Software") return "SaaS Provider";
  if (category === "Office & Operations") return "Office Supplies";
  if (category === "Travel & Transport") return "Cab / Travel";
  return "General Vendor";
}

function extractRecurrence(text: string, baseDate: string): { recurrence: ExpenseRecurrence; nextBillingDate: string } {
  const lower = text.toLowerCase();
  const d = new Date(baseDate);

  if (lower.includes("annual") || lower.includes("yearly") || lower.includes("per year") || lower.includes("every year")) {
    const next = new Date(d);
    next.setFullYear(next.getFullYear() + 1);
    return { recurrence: "annual", nextBillingDate: next.toISOString().slice(0, 10) };
  }

  if (lower.includes("quarterly") || lower.includes("every quarter") || lower.includes("per quarter")) {
    const next = new Date(d);
    next.setMonth(next.getMonth() + 3);
    return { recurrence: "quarterly", nextBillingDate: next.toISOString().slice(0, 10) };
  }

  // Monthly triggers: "monthly", "per month", "each month", "every month", "subscription", "recurring", "claude code"
  if (
    lower.includes("monthly") ||
    lower.includes("month") ||
    lower.includes("subscription") ||
    lower.includes("recurring") ||
    lower.includes("repeating") ||
    lower.includes("claude code") ||
    lower.includes("copilot")
  ) {
    const next = new Date(d);
    next.setMonth(next.getMonth() + 1);
    return { recurrence: "monthly", nextBillingDate: next.toISOString().slice(0, 10) };
  }

  return { recurrence: "one_time", nextBillingDate: "" };
}

function extractPaymentMethod(text: string): string {
  const lower = text.toLowerCase();
  if (lower.includes("upi") || lower.includes("gpay") || lower.includes("google pay") || lower.includes("phonepe") || lower.includes("paytm")) {
    return "UPI / GPay";
  }
  if (lower.includes("credit card") || lower.includes("card") || lower.includes("visa") || lower.includes("mastercard")) {
    return "Credit Card";
  }
  if (lower.includes("debit card")) {
    return "Debit Card";
  }
  if (lower.includes("bank") || lower.includes("neft") || lower.includes("rtgs") || lower.includes("net banking") || lower.includes("transfer")) {
    return "Bank Transfer";
  }
  if (lower.includes("cash")) {
    return "Cash";
  }
  return "Credit Card";
}

function extractDate(text: string): string {
  const lower = text.toLowerCase();
  const today = new Date();

  if (lower.includes("day before yesterday")) {
    const d = new Date(today);
    d.setDate(d.getDate() - 2);
    return d.toISOString().slice(0, 10);
  }

  if (lower.includes("yesterday")) {
    const d = new Date(today);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  }

  return today.toISOString().slice(0, 10);
}

export function parseVoiceExpense(transcript: string): ParsedVoiceExpense {
  const clean = transcript.trim();
  const amount = extractAmount(clean);
  const category = extractCategory(clean);
  const vendor = extractVendor(clean, category);
  const paymentMethod = extractPaymentMethod(clean);
  const date = extractDate(clean);
  const { recurrence, nextBillingDate } = extractRecurrence(clean, date);

  // Clean description
  let desc = clean;
  if (vendor && amount > 0) {
    if (recurrence === "monthly") {
      desc = `${vendor} Monthly Subscription`;
    } else if (recurrence === "annual") {
      desc = `${vendor} Annual Subscription`;
    } else {
      desc = `${vendor} - ${category}`;
    }
  }

  const confidence =
    (amount > 0 ? 0.4 : 0) +
    (category !== "Software" ? 0.3 : 0.2) +
    (vendor !== "General Vendor" ? 0.3 : 0.1) +
    (recurrence !== "one_time" ? 0.1 : 0);

  return {
    amount,
    category,
    vendor,
    description: desc,
    date,
    recurrence,
    nextBillingDate,
    paymentMethod,
    notes: `Voice input: "${clean}"`,
    rawTranscript: clean,
    confidence: Math.min(1, confidence),
  };
}
